// Menus — per-user, per-day meal plans over a date range.
//
// Menus are PRIVATE (PK = the caller's Cognito Identity ID) under SK
// `menu#<date>` where <date> is a local YYYY-MM-DD calendar date, so there is
// exactly one menu row per day. A menu holds a list of rich menu ENTRIES (see
// model/menu-edit.js for the entry shape — each carries a stable menuEntryId and
// per-ingredient sourcing) plus an optional bonusKj:
//
//   { entries: [ <menu entry> ], bonusKj? }
//
// `bonusKj` is reserved for exercise allowance — kJ, consistent with
// the rest of the app's energy units (goalKj, kj100g, etc.).
//
// The generator (generateMenuRange) fills each day toward the user's daily
// energy goal (±tolerance). For each placed serving it SOURCES the entry via the
// shared menu-edit path: consume recipe-stock (leftovers) first, else make/buy
// the ingredients (soonest-expiry-first, whole packs). It returns the day plans,
// the flat list of stock OPS to apply on approval, and a pack-level shopping
// list aggregated from the buy ops.

import { getItem, listItems, updateItem, deleteItem } from './db.js';
import {
  recipeNutrition,
  hasTag,
  virtualRecipeForIngredient,
  componentRef,
  isRecipeComponent,
  INGREDIENT_RECIPE_PREFIX,
} from './recipes.js';
import { tagsMatchIngredients } from './strategies.js';
import { addStockEntry, updateStockEntry, deleteStockEntry } from './stock.js';
import { isBuyable, isAlwaysAvailable } from './ingredients.js';
import {
  poolsFromStock,
  clonePools,
  sourceEntry,
  consumeRecipeStock,
  canChangeServings,
  setEntryServings,
} from './menu-edit.js';
import {
  extraServeChance,
  pickWeightedCandidate,
  candidateProbabilities,
  planningInstant,
  weightedPickIndex,
  expiryScore,
  drawExpiryScore,
  sourceScore,
  FACTOR_WEIGHTS,
  SOURCE_THRESHOLDS,
} from './menu-factors.js';

/** SK prefix for menu (per-day plan) records in a user's private partition. */
export const MENU_PREFIX = 'menu#';

/** Slot tag for entries that don't fill a named strategy slot (fillers / manual
 * adds). Named slots use their strategy tag (e.g. 'Lunch'). */
export const OTHER_SLOT = 'Other';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Build the menu itemId for a local YYYY-MM-DD date string. */
export function menuIdForDate(date) {
  return `${MENU_PREFIX}${date}`;
}

/** The YYYY-MM-DD date a menu row is for, derived from its itemId. */
export function dateForMenuId(itemId) {
  return itemId.startsWith(MENU_PREFIX) ? itemId.slice(MENU_PREFIX.length) : itemId;
}

/**
 * Format a Date as a local-time YYYY-MM-DD string (see inventory.js for why we
 * avoid toISOString() here — it shifts to UTC and can roll the day back).
 */
export function formatLocalDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * The list of local YYYY-MM-DD date strings for `days` consecutive days
 * starting at `startDate` (a YYYY-MM-DD string), inclusive.
 */
export function dateRange(startDate, days) {
  const start = new Date(`${startDate}T00:00:00`);
  const out = [];
  for (let i = 0; i < days; i += 1) {
    out.push(formatLocalDate(new Date(start.getTime() + i * MS_PER_DAY)));
  }
  return out;
}

/** List all menu rows for the signed-in user. */
export function listMenus(doc, userId) {
  return listItems(doc, userId, MENU_PREFIX);
}

/** Fetch one day's menu by date (YYYY-MM-DD), or null if none exists. */
export function getMenu(doc, userId, date) {
  return getItem(doc, userId, menuIdForDate(date));
}

/**
 * Upsert a single day's menu. `menu` is { entries:[{recipeRef,servings,toBuy?}],
 * bonusKj? }. Returns the stored shape (including its itemId + date).
 */
export async function saveMenu(doc, userId, date, menu) {
  const stored = { entries: (menu.entries ?? []).map(sanitiseEntry) };
  if (menu.bonusKj) stored.bonusKj = Number(menu.bonusKj);
  const run = sanitiseRun(menu.run);
  if (run) stored.run = run;
  await updateItem(doc, userId, menuIdForDate(date), stored);
  return { userId, itemId: menuIdForDate(date), date, ...stored };
}

/**
 * Normalise the generation `run` settings stored on a menu day so later top-up /
 * regenerate can reuse the same strategy + goal. Returns null when there's
 * nothing meaningful to store (so we don't persist an empty object).
 */
function sanitiseRun(run) {
  if (!run) return null;
  const out = {};
  if (run.strategyRef) out.strategyRef = run.strategyRef;
  if (Number.isFinite(run.goalKj)) out.goalKj = Number(run.goalKj);
  if (Number.isFinite(run.underTolerancePct)) out.underTolerancePct = Number(run.underTolerancePct);
  if (Number.isFinite(run.overTolerancePct)) out.overTolerancePct = Number(run.overTolerancePct);
  return Object.keys(out).length ? out : null;
}

/**
 * Normalise a menu entry for storage: keep the rich Phase-4 fields and coerce
 * servings to a number, dropping any undefined optional keys so the persisted
 * row stays tidy.
 */
function sanitiseEntry(e) {
  // A "quick energy" entry is a self-contained ad-hoc energy log (e.g. a meal
  // out): a label + kJ, no recipe/ingredients/stock. It carries no recipeRef.
  if (e.kind === 'quick') {
    const entry = {
      kind: 'quick',
      label: String(e.label ?? '').trim(),
      quickKj: Number(e.quickKj) || 0,
      servings: Number(e.servings) || 1,
    };
    if (e.menuEntryId) entry.menuEntryId = e.menuEntryId;
    if (e.slot) entry.slot = e.slot;
    if (e.eaten) entry.eaten = Number(e.eaten);
    return entry;
  }

  const entry = { recipeRef: e.recipeRef, servings: Number(e.servings) };
  if (e.menuEntryId) entry.menuEntryId = e.menuEntryId;
  if (e.requiresPrep !== undefined) entry.requiresPrep = e.requiresPrep;
  if (e.servingsMade !== undefined) entry.servingsMade = e.servingsMade;
  // The "Change serving count" per-recipe kept-yield overrides, keyed by recipeRef
  // (top-level recipe + any nested sub-recipe cooked by this entry). madeFor /
  // madeServingsOf prefer an override over the recipe's own servingsMade. MUST
  // persist or the kept yields revert to recipe defaults on reload. Each value is
  // kept only when a valid ≥1 integer; the edit UI clears entries equal to the
  // recipe default before saving, so a clean (unmodified) entry stores nothing.
  if (e.cookOverrides && typeof e.cookOverrides === 'object') {
    const clean = {};
    for (const [ref, val] of Object.entries(e.cookOverrides)) {
      if (Number(val) >= 1) clean[ref] = Math.floor(Number(val));
    }
    if (Object.keys(clean).length > 0) entry.cookOverrides = clean;
  }
  if (e.preparedBy) entry.preparedBy = e.preparedBy;
  // Real-time stock: the prepare/eat lifecycle markers. `prepared` flags
  // a cook entry whose batch has been cooked (deposited as recipe-stock);
  // `cookedBatches` records the recipe-stock batches it created so a later removal
  // can reason about them. These MUST persist or a prepared entry reverts to
  // 'planned' on reload (and its batch would look re-cookable).
  if (e.prepared) entry.prepared = true;
  if (e.cookedBatches) entry.cookedBatches = e.cookedBatches;
  // Sibling-owned recipe-stock batches this entry drew a sub-recipe's leftovers
  // from — persisted so a later edit can pair this consumer with its cook.
  if (e.usesBatches) entry.usesBatches = e.usesBatches;
  if (e.slot) entry.slot = e.slot;
  if (e.ingredients) entry.ingredients = e.ingredients;
  if (e.fromRecipeStock) entry.fromRecipeStock = e.fromRecipeStock;
  if (e.eaten) entry.eaten = Number(e.eaten); // servings already eaten (0 omitted)
  return entry;
}

/** Delete a single day's menu by date. */
export function deleteMenu(doc, userId, date) {
  return deleteItem(doc, userId, menuIdForDate(date));
}

/** SK prefix for archived (consumed) menu records. Excluded from live `menu#`
 * queries (different prefix), so the history doesn't slow day fetches. */
export const ARCHIVED_MENU_PREFIX = 'archived_menu#';

/**
 * Build an eaten-only snapshot of a day for archiving: keep ONE record per entry
 * that had servings eaten, with `servings` set to the eaten count and the
 * volatile sourcing details (fromStock/fromRecipeStock) dropped — we only record
 * WHAT was eaten, not how it was sourced. Returns [] when nothing was eaten.
 *
 * When `recipesById`/`ingredientsById` are supplied, each entry is also stamped
 * with a point-in-time `name` and per-serve nutrition (`perServeKj` +
 * `perServeProtein`/`Fat`/`Carbs`, see `archivedNutritionFields`) so the record
 * stays meaningful for historical display even after the referenced
 * recipe/ingredient is renamed or deleted. Quick-energy entries carry energy only.
 */
export function eatenSnapshot(menu, recipesById = null, ingredientsById = null) {
  const out = [];
  for (const e of menu?.entries ?? []) {
    const eaten = Math.max(0, Number(e.eaten) || 0);
    if (eaten <= 0) continue;

    // Quick-energy entries have no recipe; freeze their label + kJ directly so the
    // archive shows them (and totals them) without any recipe lookup.
    if (e.kind === 'quick') {
      const rec = {
        kind: 'quick',
        servings: eaten,
        name: e.label ?? '',
        perServeKj: Number(e.quickKj) || 0,
      };
      if (e.slot) rec.slot = e.slot;
      out.push(rec);
      continue;
    }

    const rec = { recipeRef: e.recipeRef, servings: eaten };
    if (e.slot) rec.slot = e.slot;
    if (recipesById) {
      const recipe = recipesById.get(e.recipeRef);
      if (recipe?.name) rec.name = recipe.name;
      Object.assign(rec, archivedNutritionFields(recipe, ingredientsById, recipesById));
    }
    out.push(rec);
  }
  return out;
}

/**
 * The point-in-time per-serve nutrition frozen on an archived entry:
 * `{ perServeKj, perServeProtein, perServeFat, perServeCarbs }` (kJ / grams).
 * All four are null when the recipe is missing or its nutrition can't be fully
 * resolved, so readers show "unknown" rather than a misleadingly-low figure.
 * Shared by `eatenSnapshot` and the one-off macro backfill of older records.
 */
export function archivedNutritionFields(recipe, ingredientsById, recipesById) {
  if (recipe && ingredientsById) {
    const { perServing, complete } = recipeNutrition(recipe, ingredientsById, recipesById);
    if (complete) {
      return {
        perServeKj: perServing.kj,
        perServeProtein: perServing.protein,
        perServeFat: perServing.fat,
        perServeCarbs: perServing.carbs,
      };
    }
  }
  return { perServeKj: null, perServeProtein: null, perServeFat: null, perServeCarbs: null };
}

/** The energy-relevant subset of a day's `run` to freeze on an archive, so the
 * historical goal/tolerance is known even if global settings later change.
 * Drops `strategyRef` (meaningless for history). Returns null when empty. */
function archivedRun(run) {
  if (!run) return null;
  const out = {};
  if (Number.isFinite(run.goalKj)) out.goalKj = Number(run.goalKj);
  if (Number.isFinite(run.underTolerancePct)) out.underTolerancePct = Number(run.underTolerancePct);
  if (Number.isFinite(run.overTolerancePct)) out.overTolerancePct = Number(run.overTolerancePct);
  return Object.keys(out).length ? out : null;
}

/**
 * Archive the eaten servings of a day. Writes an `archived_menu#<date>#<ISO ts>`
 * row (unique per archive, so generating + finishing another menu on the SAME
 * date keeps a distinct history record). Alongside the eaten-only snapshot it
 * freezes point-in-time data for historical display: each entry's `name` +
 * `perServeKj`, the day's exercise `bonusKj`, the energy-only `run` (goal +
 * tolerances), and `surplusKj` — the banked surplus balance BEFORE this day was
 * scored (i.e. what was available that morning; history adds it to the day's
 * upper limit). Returns the stored archive (or null when nothing was eaten, so we
 * never write an empty archive).
 */
export async function archiveEatenMenu(
  doc,
  userId,
  date,
  menu,
  recipesById = null,
  ingredientsById = null,
  { now = new Date(), surplusKj = null } = {},
) {
  const entries = eatenSnapshot(menu, recipesById, ingredientsById);
  if (entries.length === 0) return null;
  const archivedAt = now.toISOString();
  const itemId = `${ARCHIVED_MENU_PREFIX}${date}#${archivedAt}`;
  const stored = { date, archivedAt, entries };
  const bonusKj = Math.max(0, Number(menu?.bonusKj) || 0);
  if (bonusKj > 0) stored.bonusKj = bonusKj;
  const run = archivedRun(menu?.run);
  if (run) stored.run = run;
  // 0 is stored (a real "nothing banked"); absent means "not recorded".
  if (surplusKj !== null && Number.isFinite(Number(surplusKj))) {
    stored.surplusKj = Math.max(0, Number(surplusKj));
  }
  await updateItem(doc, userId, itemId, stored);
  return { userId, itemId, ...stored };
}

/**
 * The covered calendar date (YYYY-MM-DD) of an archived-menu record. Prefers the
 * stored `date` field, falling back to parsing it out of the
 * `archived_menu#<date>#<ISO ts>` itemId — so the date is still recoverable even
 * if the stored shape or ID structure changes in future.
 */
export function archivedMenuDate(record) {
  if (record?.date) return record.date;
  const id = record?.itemId ?? '';
  if (!id.startsWith(ARCHIVED_MENU_PREFIX)) return null;
  const rest = id.slice(ARCHIVED_MENU_PREFIX.length);
  const hash = rest.indexOf('#');
  return hash === -1 ? rest : rest.slice(0, hash);
}

/** List a user's archived (consumed) menu records. */
export function listArchivedMenus(doc, userId) {
  return listItems(doc, userId, ARCHIVED_MENU_PREFIX);
}

/** Delete one archived-menu record by its full itemId (used to undo a finish). */
export function deleteArchivedMenu(doc, userId, itemId) {
  return deleteItem(doc, userId, itemId);
}

/**
 * Finalise a generated menu run: persist each day's menu AND apply the run's
 * stock `ops` to the kitchen state. This is the "approve" step of the draft →
 * review → finalise flow; it makes stock catch up with what the menu will use.
 *
 * Virtual ingredient entries (`recipe#ingredient:…`) are persisted as-is; their
 * recipeRef resolves back to the ingredient at read time via the page's recipe map.
 *
 * @param menus Map<date, menu> (the generated day plans)
 * @param ops   the flat op list from generateMenuRange (see applyStockOps)
 */
export async function finaliseMenuRun(doc, userId, menus, ops) {
  const tasks = [];
  for (const [date, menu] of menus) tasks.push(saveMenu(doc, userId, date, menu));
  tasks.push(applyStockOps(doc, userId, ops));
  await Promise.all(tasks);
}

/**
 * Apply a flat list of stock ops (from generateMenuRange / menu-edit sourcing)
 * to the user's stock rows. Ops are NETTED per stockRef first so a row that is
 * added and then partly consumed in the same run results in a single write:
 *
 *   addIngredientStock / addRecipeStock — create a row at the op's stockRef
 *     (servings is the running total after consumes against it).
 *   consumeIngredient / consumeRecipeStock — decrement an existing row; a row
 *     reduced to <= 0 is deleted.
 *   returnIngredient / returnRecipeStock — increment an existing row (edit
 *     give-back; not emitted by generation).
 *   buy — NOT a stock write; it feeds the shopping list only and is ignored.
 *
 * `stockRef` is used as the row's itemId, so a generator-minted ref for a new
 * row and every consume/return that targets it line up.
 */
export async function applyStockOps(doc, userId, ops) {
  // Per-row plan: { add?: full new-row attrs, delta: net servings change,
  // recreate?: attrs to rebuild a row that returns land on if it's been deleted,
  // consumed: net servings actually consumed from this row (consumes − returns) }.
  const rows = new Map();
  const planFor = (stockRef) => {
    if (!rows.has(stockRef))
      rows.set(stockRef, { add: null, delta: 0, recreate: null, consumed: 0 });
    return rows.get(stockRef);
  };

  for (const op of ops ?? []) {
    switch (op.type) {
      case 'addIngredientStock':
        planFor(op.stockRef).add = {
          ingredientRef: op.ingredientRef,
          servings: Number(op.servings) || 0,
          obtainedDate: op.obtainedDate,
          // A bought pack: only becomes stock if a surviving entry still draws
          // from it (see the materialisation gate below).
          isBuy: true,
        };
        break;
      case 'addRecipeStock':
        planFor(op.stockRef).add = {
          recipeRef: op.recipeRef,
          servings: Number(op.servings) || 0,
          obtainedDate: op.obtainedDate,
          shelfLifeDays: op.shelfLifeDays ?? DEFAULT_LEFTOVER_DAYS,
          ...(op.preparedBy ? { preparedBy: op.preparedBy } : {}),
        };
        break;
      case 'consumeIngredient':
      case 'consumeRecipeStock': {
        const plan = planFor(op.stockRef);
        plan.delta -= Number(op.servings) || 0;
        plan.consumed += Number(op.servings) || 0;
        break;
      }
      case 'returnIngredient': {
        const plan = planFor(op.stockRef);
        plan.delta += Number(op.servings) || 0;
        plan.consumed -= Number(op.servings) || 0;
        // Remember how to rebuild the row if it was deleted when fully consumed.
        if (op.ingredientRef && !plan.recreate) {
          plan.recreate = { ingredientRef: op.ingredientRef };
        }
        break;
      }
      case 'returnRecipeStock': {
        const plan = planFor(op.stockRef);
        plan.delta += Number(op.servings) || 0;
        plan.consumed -= Number(op.servings) || 0;
        if (op.recipeRef && !plan.recreate) {
          plan.recreate = { recipeRef: op.recipeRef, shelfLifeDays: DEFAULT_LEFTOVER_DAYS };
        }
        break;
      }
      case 'buy':
        break; // shopping-list only
      default:
        break;
    }
  }

  const tasks = [];
  for (const [stockRef, { add, delta, recreate, consumed }] of rows) {
    if (add) {
      // A BOUGHT pack only becomes real stock if a surviving entry still draws
      // from it. If it was bought then fully given back in the same edit session
      // (its meal was removed or regenerated), `consumed <= 0` and nothing was
      // actually committed — buys aren't paid for until the menu is saved with the
      // meal on it — so skip it. (Cooked recipe-stock batches are NOT gated this
      // way: their surplus leftovers are legitimately stocked even when uneaten,
      // and a removed cook self-nets via its drain `consumeRecipeStock` op.)
      if (add.isBuy && consumed <= 0) continue;
      const { isBuy: _isBuy, ...addRow } = add;
      const servings = addRow.servings + delta; // delta is <= 0 for consumes
      if (servings > 0) tasks.push(addStockEntry(doc, userId, { ...addRow, servings }, stockRef));
      // else: bought then fully consumed in the same run — no row needed.
    } else if (delta !== 0) {
      // Existing row: read it to compute the new total (and delete at <= 0).
      tasks.push(adjustExistingRow(doc, userId, stockRef, delta, recreate));
    }
  }
  await Promise.all(tasks);
}

/**
 * Increment/decrement an existing stock row by `delta`, deleting it at <= 0.
 * If the row no longer exists and `delta` is positive (a give-back to a row that
 * was deleted when fully consumed), recreate it from `recreate` so it comes back
 * as a complete row (with its ingredientRef/recipeRef), not a bare {servings}.
 */
async function adjustExistingRow(doc, userId, itemId, delta, recreate = null) {
  const row = await getItem(doc, userId, itemId);
  const current = Number(row?.servings) || 0;
  const next = current + delta;
  if (next <= 0) return deleteStockEntry(doc, userId, itemId);
  if (!row && recreate) {
    return addStockEntry(
      doc,
      userId,
      { ...recreate, servings: next, obtainedDate: formatLocalDate(new Date()) },
      itemId,
    );
  }
  return updateStockEntry(doc, userId, itemId, { servings: next });
}

/**
 * Nutrition totals for a single day's menu: the sum across its entries of
 * (recipe per-serving nutrition) × (entry servings). Energy in kJ, macros in g.
 *
 * @param menu            { entries:[{recipeRef,servings}] }
 * @param recipesById     Map<recipeId, recipe>
 * @param ingredientsById Map<ingredientId, ingredient>
 * @returns { kj, protein, fat, carbs, complete }
 */
export function dayNutrition(menu, recipesById, ingredientsById) {
  const total = { kj: 0, protein: 0, fat: 0, carbs: 0 };
  let complete = true;
  for (const entry of menu?.entries ?? []) {
    const servings = Number(entry.servings) || 0;
    // A quick-energy entry contributes its kJ directly (macros unknown → 0). It
    // has no recipe, so handle it before the recipe lookup (and don't mark the
    // day incomplete for it).
    if (entry.kind === 'quick') {
      total.kj += (Number(entry.quickKj) || 0) * servings;
      continue;
    }
    const recipe = recipesById.get(entry.recipeRef);
    if (!recipe) {
      complete = false;
      continue;
    }
    const { perServing, complete: recipeComplete } = recipeNutrition(
      recipe,
      ingredientsById,
      recipesById,
    );
    if (!recipeComplete) complete = false;
    total.kj += perServing.kj * servings;
    total.protein += perServing.protein * servings;
    total.fat += perServing.fat * servings;
    total.carbs += perServing.carbs * servings;
  }
  return { ...total, complete };
}

// ---------------------------------------------------------------------------
// Menu generation
// ---------------------------------------------------------------------------
//
// The generator carries the editable per-row stock POOLS (from
// menu-edit.poolsFromStock) across the whole date range — stock drawn on day 1
// isn't available on day 2 — and SOURCES each placed serving through the shared
// menu-edit path so generation and editing behave identically:
//
//   1. consumeRecipeStock — a leftover prepared serving (recipe-stock). NO prep.
//   2. sourceEntry        — otherwise prepare the recipe now: draw its ingredient
//                           batch from stock soonest-expiry-first, BUY whole packs
//                           for any shortfall, and deposit the surplus servings as
//                           recipe-stock for later days. Requires prep.
//
// Each placement appends the entry's stock OPS to a run-wide list (applied on
// approval) and the entry itself to that day's plan. A recipe is buyable unless
// it has a component the user marked unbuyable (buyServings: 0) — superseding the
// old avoid-buy-tags mechanism.

/** Servings of a recipe currently available as recipe-stock (leftovers). */
function recipeStockServings(pools, ref, rowUsable = null) {
  const bucket = pools.recipes.get(ref);
  if (!bucket) return { servings: 0, soonestExpiry: null, expiries: [] };
  let servings = 0;
  let soonestExpiry = null;
  const expiries = [];
  for (const r of bucket.rows) {
    if (rowUsable && !rowUsable(r)) continue; // e.g. batch prepped after this day
    servings += r.servings;
    if (soonestExpiry === null) soonestExpiry = r.expiry ?? null;
    if (r.expiry instanceof Date) expiries.push(r.expiry);
  }
  return { servings, soonestExpiry, expiries };
}

/**
 * Whole servings of a recipe the current pools can make right now — the min
 * across its components of (available ÷ needed). Recurses into sub-recipe
 * components: a sub-recipe contributes its OWN makeable servings PLUS any of its
 * leftovers already in recipe-stock, divided by how many the parent batch needs.
 * `_visiting` guards against cycles (identity already forbids them, but be safe).
 */
function makeableServings(recipe, pools, ingredientsById, recipesById = null, _visiting = null) {
  let coverable = Infinity;
  let soonestExpiry = null;
  const visiting = _visiting ?? new Set();
  if (visiting.has(recipe.itemId)) return { coverable: 0, soonestExpiry: null };
  visiting.add(recipe.itemId);

  for (const c of recipe.components ?? []) {
    const need = Number(c.servings) || 0;
    if (need <= 0) continue;
    const ref = componentRef(c);

    if (isRecipeComponent(c)) {
      const sub = recipesById?.get?.(ref);
      if (!sub) return { coverable: 0, soonestExpiry: null }; // unknown → not makeable
      // Sub-recipe supply = its existing leftovers + what we could cook of it.
      const stock = recipeStockServings(pools, sub.itemId);
      const subMake = makeableServings(sub, pools, ingredientsById, recipesById, visiting);
      const have = stock.servings + subMake.coverable;
      coverable = Math.min(coverable, Math.floor(have / need));
      for (const soon of [stock.soonestExpiry, subMake.soonestExpiry]) {
        if (soon && (!soonestExpiry || soon < soonestExpiry)) soonestExpiry = soon;
      }
      continue;
    }

    const ingredient = ingredientsById.get(ref);
    if (isAlwaysAvailable(ingredient)) continue; // infinite (pantry/restaurant) — never limits
    const bucket = pools.ingredients.get(ref);
    let have = 0;
    for (const r of bucket?.rows ?? []) have += r.servings;
    coverable = Math.min(coverable, Math.floor(have / need));
    const soon = bucket?.rows[0]?.expiry ?? null;
    if (soon && (!soonestExpiry || soon < soonestExpiry)) soonestExpiry = soon;
  }
  visiting.delete(recipe.itemId);
  if (!Number.isFinite(coverable)) coverable = 0;
  return { coverable, soonestExpiry };
}

/**
 * Collect the dated expiries of the stock ONE serving of `recipe` would DRAW,
 * walking the full component tree. This is INDEPENDENT of the recipe's top-level
 * source label: even a "buy" recipe still draws whatever in-stock components it
 * has (soonest-expiry-first) and only buys the shortfall, so those expiring items
 * ARE being used up and must count toward the expiry factor.
 *
 * Per the settled design: a component that draws AT LEAST one serving of dated
 * stock contributes that stock's soonest expiry (partial stock counts); a
 * sub-recipe's own leftovers + its nested ingredient draws are FLATTENED in (each
 * dated draw is its own entry, so the average + count-bonus see them all). A
 * recipe's OWN leftovers (recipe-stock source) are added by the caller.
 *
 * Read-only: reasons about availability without mutating the pools (a close proxy
 * for a single serving's draws).
 *
 * Each draw is LABELLED so the debug panel can name it precisely (no fragile
 * date-matching): a sub-recipe's own leftover reads e.g. "Rissoles (leftover)"
 * (kind 'recipe-stock'); a nested/top-level ingredient reads by its own name
 * (kind 'ingredient').
 *
 * @returns Array<{ expiry: Date, name: string, kind: 'ingredient'|'recipe-stock' }>
 */
function drawnStockExpiries(recipe, pools, ingredientsById, recipesById = null, _visiting = null) {
  const out = [];
  const visiting = _visiting ?? new Set();
  if (visiting.has(recipe.itemId)) return out;
  visiting.add(recipe.itemId);

  for (const c of recipe.components ?? []) {
    const need = Number(c.servings) || 0;
    if (need <= 0) continue;
    const ref = componentRef(c);

    if (isRecipeComponent(c)) {
      const sub = recipesById?.get?.(ref);
      if (!sub) continue; // unknown sub-recipe → nothing drawn
      // The sub-recipe's own leftovers we'd draw (soonest first), then its nested
      // ingredient/sub draws — all flattened up (each labelled distinctly).
      const subStock = recipeStockServings(pools, sub.itemId);
      if (subStock.expiries.length) {
        out.push({
          expiry: subStock.expiries[0],
          name: `${sub.name ?? sub.itemId} (leftover)`,
          kind: 'recipe-stock',
        });
      }
      out.push(...drawnStockExpiries(sub, pools, ingredientsById, recipesById, visiting));
      continue;
    }

    const ingredient = ingredientsById.get(ref);
    if (isAlwaysAvailable(ingredient)) continue; // pantry/restaurant — not perishable pressure
    const bucket = pools.ingredients.get(ref);
    let have = 0;
    for (const r of bucket?.rows ?? []) have += r.servings;
    const soon = bucket?.rows?.[0]?.expiry ?? null;
    // Any dated stock we'd draw counts (partial stock included).
    if (soon instanceof Date && have > 0) {
      out.push({ expiry: soon, name: ingredient?.name ?? ref, kind: 'ingredient' });
    }
  }
  visiting.delete(recipe.itemId);
  return out;
}

/**
 * Classify how ONE serving of `recipe` would be sourced, for the SOURCE factor.
 * Read-only (never mutates pools). Returns the category plus the ingredient
 * coverage that drives the "buy" score:
 *
 *   • category 'on-hand' — fully available AND no cooking-with-prep is needed
 *       (the recipe's own leftovers cover it, or every component is on hand and
 *       nothing that must be cooked requires prep).
 *   • category 'make'    — fully available BUT sourcing must cook something that
 *       requires prep (mirrors sourceRecipeServings: a recipe/descendant that is
 *       actually COOKED and has requiresPrep !== false; a need met from existing
 *       leftovers is not a cook and contributes no prep).
 *   • category 'buy'     — at least one ingredient must be bought.
 *
 * Coverage = unique flattened ingredient leaves across the whole tree, deduped by
 * ref, EXCLUDING pantry staples, and excluding the ingredients of any sub-recipe
 * whose need is met by its OWN leftovers (those aren't needed). An ingredient is
 * "available" iff pool servings ≥ its (aggregated) need. availableFraction =
 * available / total (1 when there are no counted ingredients).
 *
 * @returns {{ category, availableCount, totalCount, availableFraction }}
 */
function classifySource(recipe, pools, ingredientsById, recipesById = null) {
  // Aggregate need per unique non-pantry ingredient ref across the flattened tree,
  // and note whether any cook-with-prep would occur.
  const needByIngredient = new Map(); // ref → total servings needed
  let cooksWithPrep = false;

  const walk = (r, mult, visiting) => {
    if (visiting.has(r.itemId)) return;
    visiting.add(r.itemId);
    for (const c of r.components ?? []) {
      const need = (Number(c.servings) || 0) * mult;
      if (need <= 0) continue;
      const ref = componentRef(c);
      if (isRecipeComponent(c)) {
        const sub = recipesById?.get?.(ref);
        if (!sub) {
          visiting.delete(r.itemId);
          continue;
        }
        // If the sub-recipe's need is met by its OWN leftovers, we draw those and
        // do NOT cook it — its ingredients aren't needed and it adds no prep.
        const subStock = recipeStockServings(pools, sub.itemId);
        if (subStock.servings >= need) continue;
        // Otherwise it (or the shortfall) must be cooked → prep if it requires it.
        if (sub.requiresPrep !== false) cooksWithPrep = true;
        const perBatch = Math.max(1, Number(sub.servingsMade) || 1);
        // Servings we must cook after drawing any leftovers (round up to batches
        // is irrelevant for coverage — we just need the ingredient need).
        const cookNeed = need - subStock.servings;
        const batches = Math.ceil(cookNeed / perBatch);
        walk(sub, batches, visiting);
        continue;
      }
      const ingredient = ingredientsById.get(ref);
      if (isAlwaysAvailable(ingredient)) continue; // pantry staple — excluded entirely
      needByIngredient.set(ref, (needByIngredient.get(ref) ?? 0) + need);
    }
    visiting.delete(r.itemId);
  };

  // Top level: if the recipe's OWN leftovers cover a serving, it's pre-made.
  const ownStock = recipeStockServings(pools, recipe.itemId);
  if (ownStock.servings >= 1) {
    return { category: 'on-hand', availableCount: 0, totalCount: 0, availableFraction: 1 };
  }
  // Cooking this recipe itself is a prep action if it requires prep.
  if (recipe.requiresPrep !== false) cooksWithPrep = true;
  walk(recipe, 1, new Set());

  let total = 0;
  let available = 0;
  for (const [ref, need] of needByIngredient) {
    total += 1;
    const bucket = pools.ingredients.get(ref);
    let have = 0;
    for (const row of bucket?.rows ?? []) have += row.servings;
    if (have >= need) available += 1;
  }
  const availableFraction = total > 0 ? available / total : 1;
  const fullyAvailable = available >= total; // includes the no-ingredient case
  const category = !fullyAvailable ? 'buy' : cooksWithPrep ? 'make' : 'on-hand';
  return { category, availableCount: available, totalCount: total, availableFraction };
}

/**
 * Determine the best available SOURCE for one serving of `recipe`, or null if
 * none is available without exceeding the energy cap. Does not mutate state.
 *
 * `draws` is the list of LABELLED dated stock draws this serving would take,
 * across the WHOLE component tree — the recipe's own leftover row for
 * recipe-stock, PLUS any in-stock (sub-)components it would consume, regardless
 * of whether the top-level source is make or buy (a "buy" recipe still uses up
 * its in-stock expiring components). Each is `{ expiry, name, kind }`. It feeds
 * the absolute expiry factor (which reads the dates) and the debug drill-down
 * (which reads the names). `soonestExpiry` is kept for display/driving flags.
 *
 * The `source` category ('on-hand' | 'make' | 'buy') and coverage
 * (availableFraction / counts) come from classifySource and drive the SOURCE
 * factor: on-hand = fully available & no prep; make = fully available but must
 * cook something that needs prep; buy = at least one ingredient must be bought
 * (scored linearly by how many ingredients ARE on hand).
 *
 * @returns { source:'on-hand'|'make'|'buy', soonestExpiry, draws,
 *   availableFraction, availableCount, totalCount } | null
 */
function bestSourceFor(
  recipe,
  kj,
  dayKj,
  upper,
  allowBuy,
  pools,
  ingredientsById,
  buyable,
  recipeStockUsable = null,
  recipesById = null,
) {
  if (dayKj + kj > upper) return null;
  // Labelled dated stock this serving would consume from the component tree.
  const componentDraws = drawnStockExpiries(recipe, pools, ingredientsById, recipesById);
  const cls = classifySource(recipe, pools, ingredientsById, recipesById);
  const coverage = {
    availableFraction: cls.availableFraction,
    availableCount: cls.availableCount,
    totalCount: cls.totalCount,
  };
  const stock = recipeStockServings(pools, recipe.itemId, recipeStockUsable);
  if (stock.servings >= 1) {
    // Drawn from the recipe's OWN leftovers → the soonest-expiring leftover row.
    // (No component draws — a leftover serving is pre-made → on-hand.)
    const draws = stock.expiries.length
      ? [
          {
            expiry: stock.expiries[0],
            name: `${recipe.name ?? recipe.itemId} (leftover)`,
            kind: 'recipe-stock',
          },
        ]
      : [];
    return { source: 'on-hand', soonestExpiry: stock.soonestExpiry, draws, ...coverage };
  }
  const { coverable, soonestExpiry } = makeableServings(
    recipe,
    pools,
    ingredientsById,
    recipesById,
  );
  // Fully makeable from stock → on-hand or make (classifySource decides via prep).
  if (coverable >= 1)
    return { source: cls.category, soonestExpiry, draws: componentDraws, ...coverage };
  // Buy: we still consume any in-stock components (only the shortfall is bought),
  // so those expiring items count. soonestExpiry reflects the drawn component stock.
  if (allowBuy && buyable) {
    const soonest = componentDraws.length
      ? componentDraws.reduce((a, b) => (a.expiry < b.expiry ? a : b)).expiry
      : null;
    return { source: 'buy', soonestExpiry: soonest, draws: componentDraws, ...coverage };
  }
  return null;
}

/**
 * Whether a recipe can be bought as a unit: every non-pantry-staple ingredient
 * (recursively through sub-recipes) must be buyable (buyServings > 0). A recipe
 * with an unbuyable ingredient anywhere (e.g. a Lite n Easy meal, buyServings: 0)
 * can still be eaten from stock/made but never bought. `_visiting` guards cycles.
 */
function recipeBuyableFn(recipe, ingredientsById, recipesById = null, _visiting = null) {
  const visiting = _visiting ?? new Set();
  if (visiting.has(recipe.itemId)) return false;
  visiting.add(recipe.itemId);
  const ok = (recipe.components ?? []).every((c) => {
    if (isRecipeComponent(c)) {
      const sub = recipesById?.get?.(componentRef(c));
      return sub ? recipeBuyableFn(sub, ingredientsById, recipesById, visiting) : false;
    }
    const ing = ingredientsById.get(componentRef(c));
    return isAlwaysAvailable(ing) || isBuyable(ing);
  });
  visiting.delete(recipe.itemId);
  return ok;
}

/**
 * Generate a per-day menu over a date range using a per-day strategy.
 *
 * A strategy is `{ slots:[{tag,count}], fillTags:[tag,…] }`. For each day we
 * first fill the explicit slots (e.g. 1 Breakfast, 1 Lunch, 1 Dinner), then top
 * the day up toward the calorie goal from the pool of items matching ANY of
 * `fillTags`. In BOTH phases the pick is a WEIGHTED-RANDOM choice among viable
 * candidates, scored on the source / expiry / rating factors (see
 * menu-factors.js + bestSourceFor/classifySource) — not a hard sort. Hard rules
 * still gate viability upstream (must fit the calorie ceiling; a recipe's own
 * leftovers are always drawn before a fresh batch; rating 0 = never). Each
 * serving is sourced via the shared menu-edit path (own leftovers, else cook,
 * else buy the shortfall); stock is carried across the range.
 *
 * NOTE: the debug visualiser (explainSlotCandidates / the /debug/weights page)
 * reuses this exact scoring path, so keep selection logic in the shared
 * functions rather than inlining it here (see the coupling note in
 * menu-factors.js).
 *
 * @param opts
 * @param opts.dates           array of YYYY-MM-DD strings to plan
 * @param opts.recipes         array of recipe records (stored + virtual snacks)
 * @param opts.stock           array of stock entries (ingredient + recipe)
 * @param opts.ingredientsById Map<ingredientId, ingredient>
 * @param opts.goalKj             daily energy target (kJ)
 * @param opts.underTolerancePct  allowed shortfall below the goal, percent
 * @param opts.overTolerancePct   allowed overshoot above the goal, percent
 * @param opts.strategy        { slots:[{tag,count}], fillTags:[tag,…] }
 * @param opts.allowBuy        when false, never suggest out-of-stock items
 * @param opts.genId           () => id for menuEntryIds (injectable for tests)
 * @param opts.genStockId      () => id for new stock rows (injectable for tests)
 * @param opts.leftoverShelfLifeDays  shelf life for deposited recipe-stock
 * @param opts.rng             () => [0,1) random source (injectable for tests)
 * @returns { menus: Map<date, { entries:[…] }>, ops: [...], shoppingList: [...] }
 */
export function generateMenuRange({
  // Extra-serve rolls (fill-slot entries — see makeDayFiller). `serveRng`
  // defaults to `rng`; `shortIngredientRefs` are ingredients the EXISTING plan
  // already needs to buy (extra serves may buy more of those, never new ones).
  serveRng = null,
  shortIngredientRefs = [],
  dates,
  recipes,
  stock,
  ingredientsById,
  goalKj,
  underTolerancePct = 0,
  overTolerancePct = 10,
  strategy = { slots: [], fillTags: ['Snack'] },
  allowBuy = true,
  genId = () => crypto.randomUUID(),
  genStockId = () => crypto.randomUUID(),
  leftoverShelfLifeDays,
  rng = Math.random,
  prepDates = null,
  ratingByRef = new Map(),
  // Optional pre-built baseline pools (e.g. `editPoolsFromMenus`, which includes
  // the leftovers of existing PLANNED cooks). Cloned, so the caller's is untouched.
  // When omitted, pools are built from `stock`.
  pools: providedPools = null,
}) {
  const pools = providedPools ? clonePools(providedPools) : poolsFromStock(stock, ingredientsById);
  const ctx = precomputeCandidates(
    recipePoolFor(recipes, ingredientsById, strategy),
    ingredientsById,
  );
  const bounds = toleranceBounds(goalKj, underTolerancePct, overTolerancePct);
  const ops = [];
  // Don't eat pre-existing recipe-stock before it's prepped: `prepDates` maps a
  // batch's prepared-cook menuEntryId → its prep date, so a generated day can only
  // use a batch cooked on/before that day. (Within this run, leftovers are
  // deposited as the days are processed in order, so the freshly-deposited rows —
  // which have no entry in prepDates — are simply allowed.)
  const usableOn = (date) => makeRecipeStockUsableBy(date, prepDates);

  // The run settings stamped on every generated day so later top-up/regenerate
  // can reuse the same strategy + goal (see `run` on the menu row).
  const run = {
    strategyRef: strategy?.itemId ?? null,
    goalKj: bounds.goal,
    underTolerancePct: Number(underTolerancePct) || 0,
    overTolerancePct: Number(overTolerancePct) || 0,
  };

  const result = new Map();
  // One shortage set for the whole run: a buy on day 1 makes that ingredient
  // "already short" for every later day's extra serves.
  const shortIngredients = new Set(shortIngredientRefs ?? []);
  for (const date of dates ?? []) {
    const filler = makeDayFiller({
      date,
      entries: [],
      startKj: 0,
      pools,
      ops,
      ctx,
      bounds,
      allowBuy,
      ingredientsById,
      genId,
      genStockId,
      leftoverShelfLifeDays,
      rng,
      recipeStockUsable: usableOn(date),
      ratingByRef,
      serveRng: serveRng ?? rng,
      shortIngredients,
    });
    filler.fillSlots(strategy);
    filler.fillToGoal(strategy);
    result.set(date, { entries: filler.entries, run });
  }

  // `pools` is the post-generation stock snapshot (mutated during the run). The
  // caller reuses it as the edit-session baseline so subsequent in-session edits
  // diff correctly against what generation already consumed/produced.
  return {
    menus: result,
    ops,
    pools,
    shoppingList: shoppingListFromOps(ops, ingredientsById),
  };
}

/**
 * DEBUG / INSIGHT: explain the weighted-factor selection for ONE slot at a given
 * moment, WITHOUT placing anything. Reuses the exact generator internals
 * (recipePoolFor → precomputeCandidates → bestSourceFor + classifySource for
 * viability + the per-candidate { source, availableFraction, draws }, then the
 * menu-factors scoring) so what it reports matches what generation would do.
 *
 * COUPLING: this is why the debug page stays in sync with real generation — it
 * calls the SAME functions generateMenuRange does, never a parallel copy. Keep it
 * that way: route any new/changed scoring through bestSourceFor/classifySource +
 * menu-factors, not through the page. (See the matching note atop menu-factors.js.)
 *
 * A slot is either a named strategy slot tag (candidates = recipes with that tag)
 * or OTHER_SLOT (candidates = the strategy's fillTags pool, i.e. what fillToGoal
 * draws from). `startKj` is the running day total when the pick happens (it moves
 * the calorie ceiling that gates viability), letting you inspect a slot early
 * (empty day) or late (near goal).
 *
 * The factor `weights` and source `thresholds` are injectable so a what-if UI can
 * slide them and see probabilities shift; they default to the live constants
 * (FACTOR_WEIGHTS / SOURCE_THRESHOLDS).
 *
 * PURE — clones the pools internally, so nothing is consumed.
 *
 * Each candidate also carries drill-down DETAIL objects so a debug UI can explain
 * where each factor score comes from:
 *   • sourceDetail — { source, leftoverServings, components: [{ ref, name, kind,
 *       need, have, source }] } (why on-hand / make / buy),
 *   • expiryDetail — { draws: [{ name, kind, expiry, days, score }], count,
 *       average, countBonus, score, soonestExpiry } (each labelled dated draw's
 *       per-item score on the log curve, plus the averaged + count-boosted final
 *       score; kind is 'ingredient' or 'recipe-stock'), and
 *   • ratingDetail — { rating, origin: 'explicit'|'inherited'|'default',
 *       inheritedFrom?, defaultValue? } (why the rating is what it is).
 *
 * `ratingDefault` (the dynamic default from resolveRatings) and `explicitRefs`
 * (a Set of refs whose rating is explicitly stored) are optional inputs used only
 * to explain rating provenance; scoring is unchanged without them.
 *
 * @returns {{
 *   candidates: Array<{ recipe, source, availableFraction, availableCount,
 *     totalCount, soonestExpiry, kj, rating, scores: { source, expiry, rating },
 *     weight, probability, sourceDetail, expiryDetail, ratingDetail }>,
 *   weights, thresholds, totalWeight
 * }} candidates sorted by descending probability (viable only).
 */
export function explainSlotCandidates({
  slot = OTHER_SLOT,
  recipes,
  stock,
  // Optional pre-built pools (e.g. a forward-projected residual from
  // poolsAfterMenus). When given, it's used instead of building from `stock`;
  // it is cloned internally so the probing here never mutates the caller's pool.
  pools: providedPools = null,
  ingredientsById,
  strategy = { slots: [], fillTags: [] },
  goalKj,
  underTolerancePct = 0,
  overTolerancePct = 10,
  startKj = 0,
  allowBuy = true,
  ratingByRef = new Map(),
  ratingDefault = null,
  explicitRefs = new Set(),
  weights = FACTOR_WEIGHTS,
  thresholds = SOURCE_THRESHOLDS,
  // The day being planned (YYYY-MM-DD) — expiry is scored as of that day, like
  // the generator. Omitted → now.
  date = null,
}) {
  const pools = providedPools ? clonePools(providedPools) : poolsFromStock(stock, ingredientsById);
  const ctx = precomputeCandidates(
    recipePoolFor(recipes, ingredientsById, strategy),
    ingredientsById,
  );
  const bounds = toleranceBounds(goalKj, underTolerancePct, overTolerancePct);
  const ingById = ingredientsById ?? new Map();

  const ratingOf = (recipe) => {
    const r = ratingByRef.get(recipe.itemId);
    return typeof r === 'number' ? r : 0.5;
  };
  const isBanned = (recipe) => ratingByRef.get(recipe.itemId) === 0;

  // The candidate pool for this slot mirrors the filler: a named slot draws from
  // that tag; the fill/Other slot draws from the strategy's fillTags.
  const pool =
    slot && slot !== OTHER_SLOT ? ctx.byTag(slot) : ctx.byAnyTag(strategy?.fillTags ?? []);

  // Build viable candidates exactly as makeDayFiller.viableCandidates does.
  const viable = [];
  for (const recipe of pool) {
    if (isBanned(recipe)) continue;
    const kj = ctx.recipeKj.get(recipe.itemId) ?? 0;
    const src = bestSourceFor(
      recipe,
      kj,
      startKj,
      bounds.upper,
      allowBuy,
      pools,
      ingById,
      ctx.isRecipeBuyable(recipe),
      null,
      ctx.recipeById,
    );
    if (!src) continue;
    // Read-only provenance for the drill-down detail (mirrors bestSourceFor +
    // makeableServings without consuming anything).
    const detail = explainSourceDetail(
      recipe,
      src.source,
      pools,
      ingById,
      ctx.recipeById,
      ctx.isRecipeBuyable(recipe),
    );
    viable.push({ recipe, src, kj, rating: ratingOf(recipe), detail });
  }

  // A single reference instant so the display breakdown matches the scoring.
  const now = planningInstant(date);
  // Score via the shared factor model (so weights AND the two-stage pick odds
  // match generation exactly).
  const { weights: weightArr, probabilities } = candidateProbabilities(
    viable.map((v) => ({
      source: v.src.source,
      availableFraction: v.src.availableFraction ?? 0,
      expiries: (v.src.draws ?? []).map((d) => d.expiry),
      rating: v.rating,
    })),
    weights,
    thresholds,
    now,
  );

  const total = weightArr.reduce((s, w) => s + (w > 0 ? w : 0), 0);
  const candidates = viable.map((v, i) => {
    const sScore = sourceScore(v.src.source, v.src.availableFraction ?? 0, thresholds);
    const rScore = clamp01(v.rating);
    // Absolute per-candidate expiry score from the dated draws this serving takes.
    const eScore = expiryScore(
      (v.src.draws ?? []).map((d) => d.expiry),
      now,
    );
    return {
      recipe: v.recipe,
      source: v.src.source,
      availableFraction: v.src.availableFraction ?? 0,
      availableCount: v.src.availableCount ?? 0,
      totalCount: v.src.totalCount ?? 0,
      soonestExpiry: v.src.soonestExpiry ?? null,
      kj: v.kj,
      rating: v.rating,
      scores: { source: sScore, expiry: eScore, rating: rScore },
      weight: weightArr[i],
      probability: probabilities[i],
      sourceDetail: v.detail,
      expiryDetail: buildExpiryDetail(v.src.draws ?? [], now),
      ratingDetail: ratingDetailFor(v.recipe, v.rating, {
        explicitRefs,
        ratingDefault,
        ingredientsById: ingById,
      }),
    };
  });
  candidates.sort((a, b) => b.probability - a.probability);
  return { candidates, weights, thresholds, totalWeight: total };
}

/**
 * Build the drill-down expiry detail for a candidate from its LABELLED draws
 * (`{ expiry, name, kind }` from bestSourceFor). For each draw: its name/kind,
 * expiry, whole-days-to-expiry, and per-draw log-curve score; plus the aggregate
 * (average, count, count-bonus multiplier, and final score) — mirroring
 * menu-factors.expiryScore. A candidate with no dated draws → score 0. No fragile
 * date-matching: names come straight from the tree walk, so each draw (a
 * sub-recipe leftover, a nested ingredient, a top-level ingredient) is labelled
 * distinctly.
 */
function buildExpiryDetail(srcDraws, now) {
  const dated = (srcDraws ?? []).filter((d) => d && d.expiry instanceof Date);
  const draws = dated.map((d) => ({
    name: d.name ?? null,
    kind: d.kind ?? null,
    expiry: d.expiry,
    days: Math.floor((d.expiry.getTime() - now.getTime()) / (24 * 60 * 60 * 1000)),
    score: drawExpiryScore(d.expiry, now),
  }));
  const n = draws.length;
  const avg = n ? draws.reduce((s, d) => s + d.score, 0) / n : 0;
  const countBonus = n ? 1 + 0.15 * (n - 1) : 1;
  const dates = dated.map((d) => d.expiry);
  return {
    draws,
    count: n,
    average: avg,
    countBonus,
    score: expiryScore(dates, now),
    soonestExpiry: dates.length ? dates.reduce((a, b) => (a < b ? a : b)) : null,
  };
}

/**
 * Read-only source provenance for a candidate: WHY it resolves to on-hand /
 * make / buy. Mirrors bestSourceFor + makeableServings but records per-component
 * findings instead of a single min. Never mutates the pools. (The per-component
 * `source` field below is still 'leftover'/'stock'/'make'/'buy'/'pantry' — that
 * describes how each COMPONENT is obtained, distinct from the recipe-level
 * on-hand/make/buy category.)
 *
 * @returns {{ source, leftoverServings, components: Array<{ ref, name, kind,
 *   need, have, source: 'leftover'|'stock'|'make'|'buy'|'pantry'|'unknown',
 *   expiry: Date|null }> }}
 */
function explainSourceDetail(recipe, source, pools, ingredientsById, recipesById, buyable) {
  const stock = recipeStockServings(pools, recipe.itemId);
  const leftoverServings = stock.servings;

  // On-hand via the recipe's OWN leftovers: it's pre-made, so show a single
  // leftover line rather than walking components. (An 'on-hand' recipe WITHOUT
  // its own leftovers — a no-prep recipe fully covered by ingredient stock —
  // falls through to the component walk below, which is what we want.)
  if (leftoverServings >= 1) {
    return {
      source,
      leftoverServings,
      components: [
        {
          ref: recipe.itemId,
          name: recipe.name ?? recipe.itemId,
          kind: 'recipe-stock',
          need: 1,
          have: leftoverServings,
          source: 'leftover',
          expiry: stock.soonestExpiry ?? null,
        },
      ],
    };
  }

  // Make / buy: walk the components and record how each would be sourced.
  const components = [];
  for (const c of recipe.components ?? []) {
    const ref = componentRef(c);
    const need = Number(c.servings) || 0;
    if (isRecipeComponent(c)) {
      const sub = recipesById?.get?.(ref);
      const subStock = sub
        ? recipeStockServings(pools, sub.itemId)
        : { servings: 0, soonestExpiry: null };
      const subMake = sub
        ? makeableServings(sub, pools, ingredientsById, recipesById)
        : { coverable: 0, soonestExpiry: null };
      const have = subStock.servings + subMake.coverable;
      // Label reflects how the sub-recipe would actually be sourced: its own
      // leftovers if enough, else make if its ingredients cover it, else buy (if
      // buyable), else unknown. (Previously it wrongly said "make" whenever any
      // leftovers existed, even when making wasn't achievable.)
      let subSource;
      if (!sub) subSource = 'unknown';
      else if (subStock.servings >= need) subSource = 'leftover';
      else if (subMake.coverable >= need) subSource = 'make';
      else subSource = buyable ? 'buy' : 'unknown';
      components.push({
        ref,
        name: sub?.name ?? ref,
        kind: 'recipe',
        need,
        have,
        source: subSource,
        expiry: subStock.soonestExpiry ?? subMake.soonestExpiry ?? null,
      });
      continue;
    }
    const ingredient = ingredientsById?.get?.(ref);
    if (isAlwaysAvailable(ingredient)) {
      components.push({
        ref,
        name: ingredient?.name ?? ref,
        kind: 'ingredient',
        need,
        have: Infinity,
        source: 'pantry',
        expiry: null,
      });
      continue;
    }
    const bucket = pools.ingredients.get(ref);
    let have = 0;
    for (const r of bucket?.rows ?? []) have += r.servings;
    components.push({
      ref,
      name: ingredient?.name ?? ref,
      kind: 'ingredient',
      need,
      have,
      source: have >= need ? 'stock' : buyable ? 'buy' : 'unknown',
      expiry: bucket?.rows?.[0]?.expiry ?? null,
    });
  }
  return { source, leftoverServings, components };
}

/**
 * Explain a candidate's effective rating: whether it's an explicit stored value,
 * inherited from a source ingredient (virtual ingredient-recipes), or the dynamic
 * default applied to unrated items.
 */
function ratingDetailFor(recipe, rating, { explicitRefs, ratingDefault, ingredientsById }) {
  const ref = recipe.itemId;
  // A virtual ingredient-recipe carries the ingredient's id after the prefix.
  const isVirtual = typeof ref === 'string' && ref.startsWith(INGREDIENT_RECIPE_PREFIX);
  const ingredientId = isVirtual ? ref.slice(INGREDIENT_RECIPE_PREFIX.length) : null;
  const ingredient = ingredientId ? ingredientsById?.get?.(ingredientId) : null;

  if (explicitRefs?.has?.(ref)) {
    return { rating, origin: 'explicit' };
  }
  if (isVirtual && explicitRefs?.has?.(ingredientId)) {
    return {
      rating,
      origin: 'inherited',
      inheritedFrom: ingredient?.name ?? ingredientId,
    };
  }
  return {
    rating,
    origin: 'default',
    defaultValue: typeof ratingDefault === 'number' ? ratingDefault : rating,
  };
}

/**
 * DEBUG / INSIGHT: run `count` independent weighted picks over the candidates
 * produced by explainSlotCandidates, returning how many times each recipe was
 * picked. Draws by each candidate's two-stage `probability` (the SAME odds the
 * generator picks by), falling back to its raw `weight` when absent, via the same
 * weightedPickIndex. PURE (injectable rng). No candidates → an empty tally.
 *
 * @param candidates the `candidates` array from explainSlotCandidates
 * @returns {{ counts: Map<recipeRef, { recipe, count }>, total }}
 */
export function simulateSlotPicks(candidates, { count = 1000, rng = Math.random } = {}) {
  const counts = new Map();
  const n = candidates?.length ?? 0;
  const total = Math.max(0, Math.floor(Number(count) || 0));
  if (n === 0 || total === 0) return { counts, total: 0 };
  const weights = candidates.map((c) => {
    const w = typeof c.probability === 'number' ? c.probability : c.weight;
    return w > 0 ? w : 0;
  });
  for (let i = 0; i < total; i += 1) {
    const idx = weightedPickIndex(weights, rng);
    if (idx < 0) continue;
    const c = candidates[idx];
    const ref = c.recipe.itemId;
    const cur = counts.get(ref) ?? { recipe: c.recipe, count: 0 };
    cur.count += 1;
    counts.set(ref, cur);
  }
  return { counts, total };
}

/** Local [0,1] clamp for display scores. */
function clamp01(v) {
  return typeof v === 'number' && Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0;
}

/**
 * Build empty menu days for manual curation: the same per-day `run` snapshot the
 * generator stamps (strategy + goal + tolerances, so slots and goal status still
 * render), but with NO entries — the user fills each day by hand. Pure; no stock
 * touched, so the caller pairs it with an empty ops list + a fresh pools baseline.
 *
 * @returns Map<date, { entries: [], run }>
 */
export function buildEmptyDays({
  dates,
  strategy = null,
  goalKj,
  underTolerancePct = 0,
  overTolerancePct = 10,
}) {
  const run = {
    strategyRef: strategy?.itemId ?? null,
    goalKj: Number(goalKj) || 0,
    underTolerancePct: Number(underTolerancePct) || 0,
    overTolerancePct: Number(overTolerancePct) || 0,
  };
  const result = new Map();
  for (const date of dates ?? []) result.set(date, { entries: [], run });
  return result;
}

/**
 * Top up a SINGLE existing day toward its goal. Unlike a fresh generation it
 * accounts for what's ALREADY on the day:
 *   1. fill any of the strategy's explicit slots not yet satisfied by existing
 *      entries (so a half-built day gets its missing Breakfast/Lunch/etc), then
 *   2. top up toward the goal floor from the fill-tag pool.
 *
 * Pure: mutates the given `pools` and returns the entries it added plus their
 * stock ops. Used by the menu-edit "top up" action (and, with a prior remove,
 * "regenerate"). dayKj starts from the day's existing entries, so it fills the
 * remaining gap up to the goal's lower-tolerance bound (per-meal over cap honoured).
 *
 * @returns { entries: [newly added], ops: [...] }
 */
export function topUpDay({
  serveRng = null, // extra-serve rolls (defaults to rng) — see generateMenuRange
  shortIngredientRefs = [],
  date,
  menu,
  recipes,
  ingredientsById,
  goalKj,
  underTolerancePct = 0,
  overTolerancePct = 10,
  strategy = { slots: [], fillTags: ['Snack'] },
  pools,
  allowBuy = true,
  genId = () => crypto.randomUUID(),
  genStockId = () => crypto.randomUUID(),
  leftoverShelfLifeDays,
  rng = Math.random,
  recipeStockUsable = null,
  ratingByRef = new Map(),
  recipesById = null,
}) {
  const ctx = precomputeCandidates(
    recipePoolFor(recipes, ingredientsById, strategy),
    ingredientsById,
  );
  const bounds = toleranceBounds(goalKj, underTolerancePct, overTolerancePct);
  const ops = [];

  const existing = menu?.entries ?? [];
  const startKj = existingDayKj(menu, ctx, recipesById, ingredientsById);

  const added = [];
  const filler = makeDayFiller({
    date,
    entries: added,
    startKj,
    pools,
    ops,
    ctx,
    bounds,
    allowBuy,
    ingredientsById,
    genId,
    genStockId,
    leftoverShelfLifeDays,
    rng,
    recipeStockUsable,
    ratingByRef,
    serveRng: serveRng ?? rng,
    shortIngredients: new Set(shortIngredientRefs ?? []),
  });
  filler.fillSlots(strategy, existing);
  filler.fillToGoal(strategy);
  return { entries: added, ops };
}

/**
 * Regenerate a SINGLE meal: place one fresh pick for `slot` on `date`, without
 * topping the whole day up to goal. The caller removes the old entry first (so
 * its stock is given back); this just fills that one slot again. `excludeRefs`
 * (typically the day's remaining recipes + the removed one) keeps the new pick
 * different and avoids slot duplicates.
 *
 * Pure: mutates `pools`, returns the single new entry (or null) + its ops.
 *
 * `excludeRefs` are recipes the new pick must always avoid (at least the meal
 * being regenerated, so the result differs). Duplicate-avoidance otherwise depends
 * on the slot: a NAMED slot also excludes the day's other meals (no duplicates
 * across named slots), but the OTHER/fill slot does NOT — fill meals may repeat a
 * recipe already on the day (and over-excluding a small fill pool can leave no
 * candidates).
 *
 * @returns { entry, ops }
 */
export function regenerateMeal({
  serveRng = null, // extra-serve rolls (defaults to rng) — see generateMenuRange
  shortIngredientRefs = [],
  date,
  menu,
  slot = OTHER_SLOT,
  excludeRefs = [],
  recipes,
  ingredientsById,
  goalKj,
  underTolerancePct = 0,
  overTolerancePct = 10,
  strategy = { slots: [], fillTags: ['Snack'] },
  pools,
  allowBuy = true,
  genId = () => crypto.randomUUID(),
  genStockId = () => crypto.randomUUID(),
  leftoverShelfLifeDays,
  rng = Math.random,
  recipeStockUsable = null,
  ratingByRef = new Map(),
  recipesById = null,
}) {
  const ctx = precomputeCandidates(
    recipePoolFor(recipes, ingredientsById, strategy),
    ingredientsById,
  );
  const bounds = toleranceBounds(goalKj, underTolerancePct, overTolerancePct);
  const ops = [];

  const existing = menu?.entries ?? [];
  const startKj = existingDayKj(menu, ctx, recipesById, ingredientsById);

  const added = [];
  const filler = makeDayFiller({
    date,
    entries: added,
    startKj,
    pools,
    ops,
    ctx,
    bounds,
    allowBuy,
    ingredientsById,
    genId,
    genStockId,
    leftoverShelfLifeDays,
    rng,
    recipeStockUsable,
    ratingByRef,
    serveRng: serveRng ?? rng,
    shortIngredients: new Set(shortIngredientRefs ?? []),
  });
  // Always exclude the explicitly-listed refs (the meal being regenerated, so the
  // result differs). For a NAMED slot also exclude the day's other meals (no
  // duplicates across named slots); the OTHER/fill slot may repeat (and a small
  // fill pool shouldn't be over-excluded). See the doc comment.
  const exclude = new Set(excludeRefs);
  if (slot && slot !== OTHER_SLOT) for (const e of existing) exclude.add(e.recipeRef);
  const entry = filler.fillOne(slot, strategy, exclude);
  return { entry, ops };
}

/**
 * Pick a random alternative recipe for regenerating a whole prepared batch: a
 * recipe OTHER than `currentRef` whose `servingsMade` is at least
 * `minServingsMade` (so it can cover every serving the batch feeds). Returns null
 * if none qualify. Pure (injectable `rng`).
 */
export function pickBatchAlternative({ recipes, currentRef, minServingsMade, rng = Math.random }) {
  const candidates = recipes.filter(
    (r) => r.itemId !== currentRef && Math.max(1, Number(r.servingsMade) || 1) >= minServingsMade,
  );
  if (candidates.length === 0) return null;
  return candidates[Math.floor(rng() * candidates.length)];
}

/**
 * Build a `recipeStockUsable(row)` predicate for sourcing on `date`: a recipe-stock
 * row is usable when its batch is prepared on/before `date`. `prepDates` maps a
 * prepared-cook menuEntryId → its prep date; a row with no `preparedBy` (pre-existing
 * stock) or an unknown one (already consumed/saved) is always usable. Shared by the
 * generator (pre-existing leftovers) and the menu-edit top-up. PURE.
 */
export function makeRecipeStockUsableBy(date, prepDates = null) {
  return (row) => {
    if (!row.preparedBy) return true; // pre-existing stock, not a planned batch
    const prepDate = prepDates?.get?.(row.preparedBy);
    if (!prepDate) return true; // unknown prep (already consumed/saved) — allow
    return prepDate <= date; // only batches cooked on/before this day
  };
}

/** Per-serving energy + buyability + tag-filtered candidate lists for a recipe
 * set — shared precompute for the generator and top-up. */
/**
 * The set of tags a strategy references (slot tags + fill tags), lowercased.
 */
function strategyTagSet(strategy) {
  const tags = [];
  for (const s of strategy?.slots ?? []) if (s?.tag) tags.push(String(s.tag).toLowerCase());
  for (const t of strategy?.fillTags ?? []) if (t) tags.push(String(t).toLowerCase());
  return new Set(tags);
}

/**
 * Build the virtual single-serve recipes an ingredient-matching strategy makes
 * eligible: one per ingredient carrying any of the strategy's tags (when the
 * strategy's `tagsMatchIngredients` flag is on). Returns [] when the flag is off
 * or nothing matches. The virtual recipe keeps the ingredient's own tags so the
 * generator's tag filters (byTag/byAnyTag) match it like any stored recipe.
 *
 * @param ingredientsById Map<ingredientId, ingredient>
 * @param strategy        the day's strategy
 */
export function virtualRecipesForStrategy(ingredientsById, strategy) {
  if (!tagsMatchIngredients(strategy)) return [];
  const wanted = strategyTagSet(strategy);
  if (wanted.size === 0) return [];
  const out = [];
  for (const ing of (ingredientsById ?? new Map()).values()) {
    if ((ing.tags ?? []).some((t) => wanted.has(String(t).toLowerCase()))) {
      out.push(virtualRecipeForIngredient(ing));
    }
  }
  return out;
}

/**
 * The recipe pool for generation: stored recipes plus any tag-matched virtual
 * recipes the strategy enables. De-duplicated by itemId (a stored recipe wins
 * over a synthesised one with the same id, which cannot normally collide).
 */
function recipePoolFor(recipes, ingredientsById, strategy) {
  const virtuals = virtualRecipesForStrategy(ingredientsById, strategy);
  if (virtuals.length === 0) return recipes ?? [];
  const seen = new Set((recipes ?? []).map((r) => r.itemId));
  return [...(recipes ?? []), ...virtuals.filter((v) => !seen.has(v.itemId))];
}

function precomputeCandidates(recipes, ingredientsById) {
  const recipeKj = new Map();
  const buyable = new Map();
  // Map for resolving sub-recipe components during nutrition roll-up.
  const byId = new Map((recipes ?? []).map((r) => [r.itemId, r]));
  for (const r of recipes ?? []) {
    recipeKj.set(r.itemId, recipeNutrition(r, ingredientsById, byId).perServing.kj);
    buyable.set(r.itemId, recipeBuyableFn(r, ingredientsById, byId));
  }
  const usable = (recipes ?? []).filter((r) => (recipeKj.get(r.itemId) ?? 0) > 0);
  return {
    recipeKj,
    recipeById: byId,
    isRecipeBuyable: (recipe) => buyable.get(recipe.itemId) !== false,
    byTag: (tag) => usable.filter((r) => hasTag(r.tags, tag)),
    byAnyTag: (tags) => {
      const wanted = (tags ?? []).map((t) => String(t).toLowerCase());
      return usable.filter((r) => wanted.some((t) => hasTag(r.tags, t)));
    },
  };
}

/**
 * Energy (kJ) already on a day, counted the SAME way the day card does
 * (`dayNutrition`): every entry's full servings — eaten or not — including
 * quick-energy entries and recipes outside the candidate pool (e.g. ignored
 * tags). `recipesById` should be the COMPLETE resolution map; the candidate pool
 * is overlaid as a fallback so callers that omit it still resolve candidates.
 */
function existingDayKj(menu, ctx, recipesById, ingredientsById) {
  const byId = new Map(ctx.recipeById);
  for (const [id, r] of recipesById ?? []) byId.set(id, r);
  return dayNutrition(menu, byId, ingredientsById).kj;
}

/** Convert goal + under/over percentages to absolute kJ bounds. */
function toleranceBounds(goalKj, underTolerancePct, overTolerancePct) {
  const goal = Math.max(0, Number(goalKj) || 0);
  const under = Math.max(0, Number(underTolerancePct) || 0) / 100;
  const over = Math.max(0, Number(overTolerancePct) || 0) / 100;
  return { goal, lower: goal * (1 - under), upper: goal * (1 + over) };
}

/**
 * Build the per-day placement engine (slots + fill) over a shared pool/ops, so
 * the same code drives a fresh generation and an incremental top-up. Tracks
 * `dayKj` from `startKj` and appends to the given `entries` array.
 */
function makeDayFiller({
  date,
  entries,
  startKj,
  pools,
  ops,
  ctx,
  bounds,
  allowBuy,
  ingredientsById,
  genId,
  genStockId,
  leftoverShelfLifeDays,
  rng,
  recipeStockUsable = null,
  ratingByRef = new Map(),
  serveRng = rng,
  shortIngredients = null,
}) {
  const ingById = ingredientsById ?? new Map();
  let dayKj = startKj;

  // Effective preference rating (0..1) for a recipe/virtual-recipe. Unrated
  // items were already mapped to the dynamic default upstream (resolveRatings);
  // anything missing here (e.g. a recipe not in the map) is treated as neutral.
  const ratingOf = (recipe) => {
    const r = ratingByRef.get(recipe.itemId);
    return typeof r === 'number' ? r : 0.5;
  };
  // A rating of exactly 0 means "never add" — a hard viability gate.
  const isBanned = (recipe) => ratingByRef.get(recipe.itemId) === 0;

  // Ingredients that already "need shopping" somewhere in the plan (seeded by the
  // caller from the existing menus) — extended with every ingredient this run
  // buys, so an extra serve may buy more of an already-short ingredient but never
  // introduce a NEW shortage. Shared across a run's days (the caller's Set).
  const short = shortIngredients ?? new Set();
  const noteBuys = (newOps) => {
    for (const op of newOps) if (op.type === 'buy') short.add(op.ingredientRef);
  };

  // Build the viable-candidate list for a set of recipes: each must have a
  // source within the day's cap and not be excluded/banned. Returns
  // [{ recipe, src, rating }] ready for the weighted pick.
  const viableCandidates = (candidates, exclude) => {
    const viable = [];
    for (const recipe of candidates) {
      if (exclude?.has(recipe.itemId)) continue;
      if (isBanned(recipe)) continue; // rating 0 → never
      const kj = ctx.recipeKj.get(recipe.itemId) ?? 0;
      const src = bestSourceFor(
        recipe,
        kj,
        dayKj,
        bounds.upper,
        allowBuy,
        pools,
        ingById,
        ctx.isRecipeBuyable(recipe),
        recipeStockUsable,
        ctx.recipeById,
      );
      if (src) viable.push({ recipe, src, rating: ratingOf(recipe) });
    }
    return viable;
  };

  // EXTRA SERVES (fill-slot entries only): after one serve of a recipe whose
  // count can change (single-serve, no-prep, ingredient-only — canChangeServings),
  // keep rolling `serveRng` against the energy-scaled extraServeChance, adding a
  // serve per success, until a roll fails or another serve would:
  //   • push the day over its upper energy bound, or
  //   • introduce a NEW "needs shopping" — buy an ingredient not already short
  //     in the plan, or leave something unsourced.
  // Each serve is tried on a CLONE of the pools via setEntryServings; on success
  // the clone's maps are adopted (no second sourcing pass, so stock-row ids stay
  // deterministic), on rejection the clone is simply dropped.
  const addExtraServes = (recipe, entry) => {
    const kj = ctx.recipeKj.get(recipe.itemId) ?? 0;
    let current = entry;
    while (dayKj + kj <= bounds.upper && serveRng() < extraServeChance(kj * current.servings)) {
      const trial = clonePools(pools);
      const res = setEntryServings({
        entry: current,
        recipe,
        servings: current.servings + 1,
        pools: trial,
        ingredientsById,
        recipesById: ctx.recipeById,
        date,
        genStockId,
      });
      const newShortage =
        res.unsourced.length > 0 ||
        res.ops.some((op) => op.type === 'buy' && !short.has(op.ingredientRef));
      if (res.entry === current || newShortage) break;
      pools.ingredients = trial.ingredients;
      pools.recipes = trial.recipes;
      ops.push(...res.ops);
      current = res.entry;
      dayKj += kj;
    }
    return current;
  };

  // Commit one serving of `recipe` from `source`: source via menu-edit, collect
  // its ops, append the entry (fill-slot entries may then grow extra serves, see
  // above). `slot` records which strategy slot it fills (the slot's tag, or
  // OTHER_SLOT for fill/manual meals) so the UI can group by slot.
  const place = (recipe, slot = OTHER_SLOT) => {
    let placed = null;
    // Consume the recipe's OWN leftovers if it has any usable — a pre-made
    // serving (this is what makes an 'on-hand' leftover free of cooking). Keyed
    // on real recipe-stock, NOT the source label (which is 'on-hand' for both a
    // leftover AND a no-prep recipe fully covered by ingredient stock).
    const own = recipeStockServings(pools, recipe.itemId, recipeStockUsable);
    if (own.servings >= 1) {
      const r = consumeRecipeStock({ recipe, servings: 1, pools, genId });
      placed = r.entry;
      ops.push(...r.ops);
      noteBuys(r.ops);
    }
    if (!placed) {
      const r = sourceEntry({
        recipe,
        servings: 1,
        pools,
        ingredientsById,
        recipesById: ctx.recipeById,
        date,
        genId,
        genStockId,
        leftoverShelfLifeDays,
      });
      placed = r.entry;
      ops.push(...r.ops);
      noteBuys(r.ops);
    }
    placed.slot = slot;
    dayKj += ctx.recipeKj.get(recipe.itemId) ?? 0;
    // Named slots (Breakfast/Lunch/…) always get exactly one serve.
    if (slot === OTHER_SLOT && canChangeServings(placed, recipe)) {
      placed = addExtraServes(recipe, placed);
    }
    entries.push(placed);
    return placed;
  };

  // Choose one serving from `candidates` by the weighted-factor model (source
  // preference, soonest-expiry, and user rating — see model/menu-factors.js),
  // commit it, and return the placed recipe/entry; or null if none are viable.
  // `exclude` skips already-used recipes (no duplicate meals in a slot). The
  // former placeBest/placeRandom split collapses into this single weighted pick:
  // hard preferences (stock>make>buy, soonest-expiry) are now factor scores, not
  // a strict sort, so a lower-ranked source can occasionally win — but a recipe's
  // OWN leftovers are still consumed before a fresh batch (via bestSourceFor).
  const placeWeighted = (candidates, exclude = null, slot = OTHER_SLOT) => {
    const viable = viableCandidates(candidates, exclude);
    // Expiry is judged as of the day being planned, not today.
    const chosen = pickWeightedCandidate(
      viable,
      rng,
      FACTOR_WEIGHTS,
      SOURCE_THRESHOLDS,
      planningInstant(date),
    );
    if (!chosen) return null;
    place(chosen.recipe, slot);
    return chosen.recipe;
  };

  return {
    entries,
    // Step 1: fill the strategy's explicit meal slots (no duplicate meal across
    // the slot phase; duplicates are fine in the fill step).
    //
    // `existingEntries` (top-up only) are meals already on the day: each one that
    // matches a slot's tag SATISFIES one of that slot's counts (so we only fill
    // the shortfall), and its recipe joins the dedup set so we don't re-place it.
    fillSlots(strategy, existingEntries = []) {
      const slotUsed = new Set();
      // Per-tag count of existing entries that can satisfy that tag's slot, plus
      // seed the dedup set with their recipes.
      const coveredByTag = new Map();
      for (const e of existingEntries) {
        slotUsed.add(e.recipeRef);
        // An entry that already records its slot covers exactly that slot's tag.
        // Legacy entries without `slot` fall back to any of their recipe's tags.
        if (e.slot && e.slot !== OTHER_SLOT) {
          const key = String(e.slot).toLowerCase();
          coveredByTag.set(key, (coveredByTag.get(key) ?? 0) + 1);
        } else if (!e.slot) {
          const recipe = ctx.recipeById.get(e.recipeRef);
          for (const tag of recipe?.tags ?? []) {
            const key = String(tag).toLowerCase();
            coveredByTag.set(key, (coveredByTag.get(key) ?? 0) + 1);
          }
        }
      }
      for (const slot of strategy?.slots ?? []) {
        const candidates = ctx.byTag(slot.tag);
        const tagKey = String(slot.tag).toLowerCase();
        let remaining = Number(slot.count) || 0;
        // Consume existing coverage for this tag before filling the rest.
        const covered = Math.min(remaining, coveredByTag.get(tagKey) ?? 0);
        coveredByTag.set(tagKey, (coveredByTag.get(tagKey) ?? 0) - covered);
        remaining -= covered;
        for (let i = 0; i < remaining; i += 1) {
          const chosen = placeWeighted(candidates, slotUsed, slot.tag);
          if (!chosen) break; // nothing suitable left for this slot
          slotUsed.add(chosen.itemId);
        }
      }
    },
    // Step 2: top up toward the goal floor from the fill-tag pool, at random.
    fillToGoal(strategy) {
      const fillCandidates = ctx.byAnyTag(strategy?.fillTags ?? []);
      while (dayKj < bounds.lower && placeWeighted(fillCandidates)) {
        /* keep filling until at/over the goal floor or no candidate fits */
      }
    },
    // Regenerate a SINGLE slot's meal: place exactly one fresh pick for `slotTag`
    // (a named strategy slot → that tag's pool via placeBest; OTHER_SLOT → the
    // fill-tag pool at random), excluding `exclude` recipes (e.g. the day's
    // current meals, so the new pick differs). Returns the new entry or null.
    fillOne(slotTag, strategy, exclude = new Set()) {
      // placeWeighted returns the chosen RECIPE (or null); the caller wants the
      // placed ENTRY, which is the last one appended.
      const pool =
        slotTag && slotTag !== OTHER_SLOT
          ? ctx.byTag(slotTag)
          : ctx.byAnyTag(strategy?.fillTags ?? []);
      const tag = slotTag && slotTag !== OTHER_SLOT ? slotTag : OTHER_SLOT;
      return placeWeighted(pool, exclude, tag) ? entries[entries.length - 1] : null;
    },
  };
}

/**
 * Aggregate the `buy` ops of a run into a pack-level shopping list, summing
 * packs per ingredient. Each item: { ingredientRef, name, packs, buyServings,
 * buyUnit }. The page formats the human quantity via units.formatBuyQuantity.
 */
export function shoppingListFromOps(ops, ingredientsById) {
  // The op list is APPEND-ONLY across an edit session: removing/regenerating a
  // meal doesn't delete its earlier `buy`; it appends give-back ops. So a naive
  // "sum every buy" over-counts once edits give bought stock back. We NET each
  // bought pack against its stock lifecycle instead.
  //
  // A `buy` and its `addIngredientStock` share a `stockRef` (see sourceEntry), so
  // per bought stockRef we track: the pack details, the bought servings, and the
  // net servings consumed from that row (consumes − returns). A pack is only on
  // the list if its row is still net-consumed after all edits; a bought pack that
  // ends up fully returned (its meal was removed) drops off entirely.
  //
  // Buys emitted BEFORE the edit session (from generation) carry the same
  // stockRef linkage, so this works across the whole run.

  // 1) Index bought stock rows and per-row net consumption.
  const boughtRows = new Map(); // stockRef -> { ingredientRef, packs, buyServings, buyUnit }
  const netConsumed = new Map(); // stockRef -> servings (consumes − returns)
  const bump = (ref, delta) => netConsumed.set(ref, (netConsumed.get(ref) || 0) + delta);

  // `buy` and `addIngredientStock` are emitted as a pair; correlate them by
  // pairing each buy with the NEXT addIngredientStock for the same ingredient.
  const pendingBuys = new Map(); // ingredientRef -> [buy op, …]
  for (const op of ops ?? []) {
    switch (op.type) {
      case 'buy': {
        const list = pendingBuys.get(op.ingredientRef) ?? [];
        list.push(op);
        pendingBuys.set(op.ingredientRef, list);
        break;
      }
      case 'addIngredientStock': {
        const list = pendingBuys.get(op.ingredientRef);
        const buy = list && list.length ? list.shift() : null;
        if (buy) {
          boughtRows.set(op.stockRef, {
            ingredientRef: op.ingredientRef,
            packs: Number(buy.packs) || 0,
            buyServings: buy.buyServings ?? null,
            buyUnit: buy.buyUnit ?? null,
          });
        }
        break;
      }
      case 'consumeIngredient':
        bump(op.stockRef, Number(op.servings) || 0);
        break;
      case 'returnIngredient':
        bump(op.stockRef, -(Number(op.servings) || 0));
        break;
      default:
        break;
    }
  }

  // 2) Keep only bought rows still net-consumed; aggregate packs per ingredient.
  const byRef = new Map();
  for (const [stockRef, row] of boughtRows) {
    if ((netConsumed.get(stockRef) || 0) <= 0) continue; // fully given back — skip
    const existing = byRef.get(row.ingredientRef);
    if (existing) existing.packs += row.packs;
    else
      byRef.set(row.ingredientRef, {
        ingredientRef: row.ingredientRef,
        name: ingredientsById?.get?.(row.ingredientRef)?.name ?? row.ingredientRef,
        packs: row.packs,
        buyServings: row.buyServings,
        buyUnit: row.buyUnit,
      });
  }
  return [...byRef.values()].filter((r) => r.packs > 0);
}

/** Default shelf life (days) for leftover recipe-stock. */
export const DEFAULT_LEFTOVER_DAYS = 4;
